import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { useUser } from '../context/UserContext';
import { getDashboard, getTasks, getUsers, getTodayCalls, getFunnel, getNeedsAttention } from '../services/api';

const priorityLabels: Record<string, string> = { low: 'נמוכה', normal: 'רגילה', high: 'גבוהה', urgent: 'דחופה' };
const statusLabels: Record<string, string> = { new: 'חדשה', in_progress: 'בטיפול', completed: 'הושלמה', cancelled: 'בוטלה' };
const priorityBadge: Record<string, string> = { urgent: 'badge-red', high: 'badge-yellow', normal: 'badge-blue', low: 'badge-gray' };
const statusBadge: Record<string, string> = { new: 'badge-blue', in_progress: 'badge-gold', completed: 'badge-green', cancelled: 'badge-gray' };

export default function Dashboard() {
  const { t } = useLang();
  const { userName, user } = useUser();
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [funnel, setFunnel] = useState<any>(null);
  const [needs, setNeeds] = useState<any>(null);
  const [myTasks, setMyTasks] = useState<any[]>([]);
  const [sentToMe, setSentToMe] = useState<any[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [todayCallsData, setTodayCallsData] = useState<any>({ total: 0, answered: 0, missed: 0 });

  useEffect(() => {
    getDashboard().then(res => setData(res.data)).catch(() => {});
    getFunnel().then(res => setFunnel(res.data)).catch(() => {});
    getNeedsAttention().then(res => setNeeds(res.data)).catch(() => {});
    getTodayCalls().then(res => setTodayCallsData(res.data)).catch(() => {});
    getUsers().then(res => {
      setAllUsers(res.data);
      // Find current user ID by name match
      const currentUser = res.data.find((u: any) => u.name === userName);
      if (currentUser) {
        getTasks({ assignedTo: currentUser.id }).then(r => setMyTasks(r.data.filter((t: any) => t.status !== 'completed' && t.status !== 'cancelled'))).catch(() => {});
        getTasks({ ccTo: currentUser.id }).then(r => setSentToMe(r.data.filter((t: any) => t.status !== 'completed' && t.status !== 'cancelled'))).catch(() => {});
      }
    }).catch(() => {});
  }, [userName]);

  if (!data) return <div className="page-section"><p>{t('loading')}</p></div>;

  const isAdmin = user.role === 'admin';
  const maxRevenue = isAdmin && data.monthlyData?.length ? Math.max(...data.monthlyData.map((m: any) => m.revenue), 1) : 1;

  const trends = data.trends || {};
  const stageLabel = (key: string) =>
    key === 'leads' ? t('leads')
    : key === 'prospects' ? t('prospects')
    : key === 'registrations' ? t('funnelRegistrations')
    : key === 'students' ? t('funnelStudents')
    : key;

  const renderTrend = (tr: any) => {
    if (!tr || typeof tr.delta !== 'number') return null;
    const d = tr.delta;
    const arrow = d > 0 ? '↑' : d < 0 ? '↓' : '→';
    const color = d > 0 ? '#2e7d32' : d < 0 ? '#c62828' : '#999';
    return (
      <div style={{ fontSize: 12, color, marginTop: 4 }}>
        {arrow} {d > 0 ? '+' : ''}{d} {t('vsLastMonth')}
      </div>
    );
  };

  const sendReminder = (phone: string) => {
    if (phone) {
      window.open('https://wa.me/972' + phone.replace(/\D/g, '').replace(/^0/, ''));
    } else {
      alert(t('noData'));
    }
  };

  // A primary KPI card: big number + month-over-month trend + secondary status line.
  const kpiCard = (icon: string, label: string, kpi: any, money = false) => {
    if (!kpi) return null;
    return (
      <div className="stat-card" style={{ cursor: 'pointer' }} onClick={() => navigate(kpi.route)}>
        <div className="icon">{icon}</div>
        <div className="value">{money ? `${(kpi.value || 0).toLocaleString()} ${t('shekel')}` : kpi.value}</div>
        <div className="label">{label}</div>
        {renderTrend({ delta: kpi.delta })}
        {kpi.secondary && kpi.secondary.count > 0 && (
          <div style={{ fontSize: 12, marginTop: 2, color: kpi.secondary.key === 'candidatesShort' ? '#666' : '#c62828' }}>
            {kpi.secondary.count} {t(kpi.secondary.key)}
          </div>
        )}
      </div>
    );
  };

  const attnIcon = (type: string) =>
    type === 'lead' ? '🎯' : type === 'payment' ? '💰' : type === 'task' ? '✅' : '📝';

  return (
    <div>
      {/* Recruitment Funnel */}
      {funnel && funnel.stages && (
      <div className="page-section" style={{ marginBottom: 20 }}>
        <h3 style={{ color: '#7B1FA2', marginBottom: 12, fontSize: 16 }}>
          {t('recruitmentFunnel')} {funnel.year}
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          {funnel.stages.map((stage: any, i: number) => (
            <React.Fragment key={stage.key}>
              {i > 0 && (
                <div style={{ textAlign: 'center', color: '#7B1FA2', fontSize: 12, minWidth: 60 }}>
                  <div style={{ fontSize: 20 }}>➜</div>
                  {typeof stage.conversionFromPrev === 'number' && (
                    <div>{stage.conversionFromPrev + '%'} {t('conversion')}</div>
                  )}
                </div>
              )}
              <div
                onClick={() => navigate(stage.route)}
                style={{
                  cursor: 'pointer', flex: '1 1 120px', minWidth: 120,
                  background: '#fff', border: '2px solid #7B1FA2', borderRadius: 8,
                  padding: '14px 10px', textAlign: 'center'
                }}
              >
                <div style={{ fontSize: 13, color: '#7B1FA2', fontWeight: 600 }}>{stageLabel(stage.key)}</div>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#333' }}>{stage.count}</div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
      )}
      {/* Primary KPI row */}
      {data.kpis && (
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        {kpiCard('🎓', t('activeStudents'), data.kpis.activeStudents)}
        {kpiCard('🎯', t('openLeads'), data.kpis.openLeads)}
        {kpiCard('💰', t('collectionThisMonth'), data.kpis.collection, true)}
        {kpiCard('✅', t('openTasks'), data.kpis.openTasks)}
      </div>
      )}
      {/* Secondary cards */}
      <div className="stats-grid" style={{ marginTop: 12 }}>
        <div className="stat-card">
          <div className="icon">📚</div>
          <div className="value">{data.activeCourses}</div>
          <div className="label">{t('activeCourses')}</div>
        </div>
        <div className="stat-card" style={{ cursor: 'pointer' }} onClick={() => navigate('/prospects')}>
          <div className="icon">📋</div>
          <div className="value">{data.activeProspects || 0}</div>
          <div className="label">{t('activeProspects')}</div>
          {renderTrend(trends.prospects)}
        </div>
        <div className="stat-card">
          <div className="icon">📅</div>
          <div className="value">{data.todayEvents.length}</div>
          <div className="label">{t('todayEvents')}</div>
        </div>
        <div className="stat-card">
          <div className="icon">📞</div>
          <div className="value">{todayCallsData.total}</div>
          <div className="label">{t('todayCalls')}</div>
        </div>
      </div>

      {/* Needs Attention */}
      {needs && (
      <div className="page-section" style={{ marginTop: 20, marginBottom: 20 }}>
        <h3 style={{ color: '#7B1FA2', marginBottom: 12, fontSize: 16 }}>
          ⚠️ {t('needsAttention')}
          {needs.counts?.total > 0 && (
            <span style={{ background: '#c62828', color: '#fff', borderRadius: 12, padding: '1px 9px', fontSize: 13, marginInlineStart: 8 }}>
              {needs.counts.total}
            </span>
          )}
        </h3>
        {needs.counts?.total === 0 ? (
          <p style={{ color: '#2e7d32', fontSize: 14 }}>{t('allClear')}</p>
        ) : (
          <>
            {needs.leadsNoFollowup?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <h4 style={{ fontSize: 14, marginBottom: 8, color: '#333' }}>{t('leadsNoFollowup')}</h4>
                {needs.leadsNoFollowup.map((row: any) => (
                  <div key={row.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '6px 0', borderBottom: '1px solid #eee' }}>
                    <span style={{ fontSize: 14 }}>{attnIcon('lead')} {row.name} {row.phone && <span dir="ltr" style={{ color: '#666' }}>{row.phone}</span>}</span>
                    <span style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-secondary" onClick={() => navigate(row.route)}>{t('openRecord')}</button>
                      <button className="btn btn-secondary" onClick={() => sendReminder(row.phone)}>{t('sendReminder')}</button>
                    </span>
                  </div>
                ))}
              </div>
            )}
            {needs.paymentsOverdue?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <h4 style={{ fontSize: 14, marginBottom: 8, color: '#333' }}>{t('paymentsOverdue')}</h4>
                {needs.paymentsOverdue.map((row: any) => (
                  <div key={row.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '6px 0', borderBottom: '1px solid #eee' }}>
                    <span style={{ fontSize: 14 }}>{attnIcon('payment')} {row.studentId} - {row.remaining?.toLocaleString()} {t('shekel')} {t('remaining')}</span>
                    <button className="btn btn-secondary" onClick={() => navigate(row.route)}>{t('openRecord')}</button>
                  </div>
                ))}
              </div>
            )}
            {needs.tasksOverdue?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <h4 style={{ fontSize: 14, marginBottom: 8, color: '#333' }}>{t('tasksOverdue')}</h4>
                {needs.tasksOverdue.map((row: any) => (
                  <div key={row.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '6px 0', borderBottom: '1px solid #eee' }}>
                    <span style={{ fontSize: 14 }}>{attnIcon('task')} {row.title} <span dir="ltr" style={{ color: '#666' }}>{row.dueDate}</span></span>
                    <button className="btn btn-secondary" onClick={() => navigate(row.route)}>{t('openRecord')}</button>
                  </div>
                ))}
              </div>
            )}
            {needs.registrationsPending?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <h4 style={{ fontSize: 14, marginBottom: 8, color: '#333' }}>{t('registrationsPending')}</h4>
                {needs.registrationsPending.map((row: any) => (
                  <div key={row.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '6px 0', borderBottom: '1px solid #eee' }}>
                    <span style={{ fontSize: 14 }}>{attnIcon('registration')} {row.name} {row.phone && <span dir="ltr" style={{ color: '#666' }}>{row.phone}</span>}</span>
                    <span style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-secondary" onClick={() => navigate(row.route)}>{t('openRecord')}</button>
                      <button className="btn btn-secondary" onClick={() => sendReminder(row.phone)}>{t('sendReminder')}</button>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      )}

      <div className={isAdmin ? "grid-2" : ""}>
        {/* Revenue Chart - Admin only */}
        {isAdmin && data.monthlyData && (
        <div className="chart-container">
          <div className="chart-title">{t('revenueChart')}</div>
          <div className="chart-bar-container">
            {data.monthlyData.map((m: any, i: number) => (
              <div key={i} className="chart-bar-wrapper">
                <div className="chart-bar-value">
                  {m.revenue > 0 ? `${(m.revenue / 1000).toFixed(1)}K` : '0'}
                </div>
                <div
                  className="chart-bar"
                  style={{ height: `${(m.revenue / maxRevenue) * 160}px` }}
                />
                <div className="chart-bar-label">{m.month}</div>
              </div>
            ))}
          </div>
        </div>
        )}

        {/* Right column */}
        <div>
          {/* Urgent Tasks */}
          <div className="page-section" style={{ marginBottom: 16 }}>
            <h3 style={{ color: '#7B1FA2', marginBottom: 12, fontSize: 16 }}>
              ⚡ {t('urgentTasks')}
            </h3>
            {data.urgentTasks.length === 0 ? (
              <p style={{ color: '#999', fontSize: 14 }}>{t('noData')}</p>
            ) : (
              data.urgentTasks.map((task: any) => (
                <div key={task.id} className="task-item">
                  <div className={`task-priority ${task.priority}`} />
                  <div className="task-title">{task.title}</div>
                  <div className="task-due">{task.dueDate}</div>
                </div>
              ))
            )}
          </div>

          {/* Today Events */}
          <div className="page-section">
            <h3 style={{ color: '#7B1FA2', marginBottom: 12, fontSize: 16 }}>
              📅 {t('todayEvents')}
            </h3>
            {data.todayEvents.length === 0 ? (
              <p style={{ color: '#999', fontSize: 14 }}>{t('noData')}</p>
            ) : (
              data.todayEvents.map((evt: any) => (
                <div key={evt.id} className="event-item">
                  <div className="event-date">{evt.time}</div>
                  <div className="event-details">
                    <h4>{evt.title}</h4>
                    <p>{evt.location}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* My Tasks & Sent To Me */}
      <div className="grid-2" style={{ marginTop: 20 }}>
        {/* My Tasks */}
        <div className="page-section">
          <h3 style={{ color: '#7B1FA2', marginBottom: 12, fontSize: 16 }}>
            📋 המשימות שלי
          </h3>
          {myTasks.length === 0 ? (
            <p style={{ color: '#999', fontSize: 14 }}>אין משימות פעילות</p>
          ) : (
            <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>כותרת</th>
                  <th>תאריך יעד</th>
                  <th>עדיפות</th>
                  <th>סטטוס</th>
                </tr>
              </thead>
              <tbody>
                {myTasks.map(task => (
                  <tr key={task.id} style={{ background: task.priority === 'urgent' ? '#ffebee' : task.priority === 'high' ? '#fff3e0' : 'inherit' }}>
                    <td><strong>{task.title}</strong></td>
                    <td dir="ltr">{task.dueDate || '-'}</td>
                    <td><span className={`badge ${priorityBadge[task.priority]}`}>{priorityLabels[task.priority]}</span></td>
                    <td><span className={`badge ${statusBadge[task.status]}`}>{statusLabels[task.status]}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>

        {/* Tasks Sent To Me */}
        <div className="page-section">
          <h3 style={{ color: '#7B1FA2', marginBottom: 12, fontSize: 16 }}>
            📨 משימות שנשלחו אלי
          </h3>
          {sentToMe.length === 0 ? (
            <p style={{ color: '#999', fontSize: 14 }}>אין משימות לידיעה</p>
          ) : (
            <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>כותרת</th>
                  <th>נוצר ע״י</th>
                  <th>תאריך יעד</th>
                  <th>סטטוס</th>
                </tr>
              </thead>
              <tbody>
                {sentToMe.map(task => (
                  <tr key={task.id}>
                    <td><strong>{task.title}</strong></td>
                    <td>{task.createdBy || '-'}</td>
                    <td dir="ltr">{task.dueDate || '-'}</td>
                    <td><span className={`badge ${statusBadge[task.status]}`}>{statusLabels[task.status]}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
